import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ListChecks, Mail, BellRing } from "lucide-react";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { WaitlistForm } from "../components/WaitlistForm";

const STEPS = [
  {
    icon: ListChecks,
    n: "01",
    t: "Join the list",
    d: "Share a few details about you and your role — we keep it simple.",
  },
  {
    icon: Mail,
    n: "02",
    t: "Get a confirmation email",
    d: "You'll get a welcome email confirming you're on the waiting list.",
  },
  {
    icon: BellRing,
    n: "03",
    t: "Hear from us when there's news",
    d: "We'll email you when access opens or there's meaningful news to share.",
  },
];

const PilotPage = () => {
  return (
    <main data-testid="pilot-page" className="relative min-h-screen bg-loudar-ink text-white">
      <Nav />

      <section
        data-testid="pilot-hero"
        className="relative isolate overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1681137063068-081072cf04b4?auto=format&fit=crop&w=1800&q=80"
            alt=""
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-loudar-ink/80" />
          <div className="absolute inset-0 bg-gradient-to-b from-loudar-ink via-transparent to-loudar-ink" />
        </div>

        <div className="container-loudar">
          <Link
            to="/"
            className="mb-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/55 transition-colors hover:text-loudar-orange"
            data-testid="pilot-back-link"
          >
            <ArrowLeft size={14} /> Back to Loudar
          </Link>

          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="label-mono" data-testid="waiting-list-eyebrow">
                LOUDAR WAITING LIST
              </span>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                data-testid="pilot-headline"
                className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-white sm:text-5xl md:text-6xl"
              >
                Be among the first to experience Loudar.
              </motion.h1>
              <p
                data-testid="pilot-intro"
                className="mt-5 text-white/70 md:text-lg"
              >
                Loudar is getting ready for the people behind the music. Join the waiting
                list, tell us about your role, and we'll email you when there's news
                about access. Joining the list does not guarantee immediate access.
              </p>

              {/* What happens next — 3 steps */}
              <div className="mt-10">
                <div className="label-mono mb-4">What happens next</div>
                <div className="space-y-3">
                  {STEPS.map((s) => {
                    const Icon = s.icon;
                    return (
                      <div
                        key={s.n}
                        data-testid={`whn-step-${s.n}`}
                        className="flex items-start gap-4 rounded-sm border border-white/10 bg-white/[0.03] p-4 backdrop-blur"
                      >
                        <div className="flex shrink-0 items-center gap-3">
                          <span className="font-mono text-xs text-loudar-orange">
                            {s.n}
                          </span>
                          <span className="grid h-9 w-9 place-items-center rounded-sm border border-loudar-orange/40 bg-loudar-orange/10 text-loudar-orange">
                            <Icon size={16} />
                          </span>
                        </div>
                        <div>
                          <div className="font-display text-lg uppercase tracking-tight text-white">
                            {s.t}
                          </div>
                          <div className="mt-1 text-sm text-white/60">{s.d}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="lg:col-span-7"
            >
              <div className="glass-card rounded-md p-6 md:p-10">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <div className="label-mono">Waiting list</div>
                    <div
                      data-testid="waiting-list-form-heading"
                      className="mt-1 font-display text-2xl uppercase tracking-tight text-white"
                    >
                      Join the waiting list
                    </div>
                  </div>
                  <div className="hidden h-12 w-12 rounded-full bg-loudar-orange/15 sm:grid place-items-center">
                    <span className="h-2 w-2 animate-pulse-glow rounded-full bg-loudar-orange" />
                  </div>
                </div>
                <WaitlistForm variant="pilot" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default PilotPage;
